/**
 * Mirrors the backend Data Service DTOs and enums.
 * Backend: com.reverse.job.portal.DataService.DTO / Enums
 */

export type DataEngine = 'MYSQL' | 'SQLSERVER' | 'POSTGRESQL';

export type ConnectionEnvironment = 'DEVELOPMENT' | 'STAGING' | 'PRODUCTION';

export type ConnectionStatus = 'ACTIVE' | 'DISABLED' | 'ERROR';

export type ConnectionTestStatus =
  | 'OK'
  | 'AUTH_FAILED'
  | 'UNREACHABLE'
  | 'DB_NOT_FOUND'
  | 'SSL_ERROR'
  | 'TIMEOUT'
  | 'DRIVER_MISSING'
  | 'UNKNOWN';

export type AccessPermission = 'READ' | 'READ_WRITE';

export type FilterOperator =
  | 'EQ'
  | 'NEQ'
  | 'CONTAINS'
  | 'STARTS_WITH'
  | 'ENDS_WITH'
  | 'GT'
  | 'GTE'
  | 'LT'
  | 'LTE'
  | 'BETWEEN'
  | 'IS_NULL'
  | 'IS_NOT_NULL'
  | 'IN';

export type QueryOrigin = 'BUILDER' | 'CONSOLE' | 'SUGGESTION' | 'ROW_MUTATION';

export type RowOperation = 'INSERT' | 'UPDATE' | 'DELETE';

export type FilterControlKind =
  | 'TEXT'
  | 'NUMBER'
  | 'DATE'
  | 'DATETIME'
  | 'BOOLEAN'
  | 'ENUM'
  | 'JSON'
  | 'BINARY'
  | 'UUID';

export type DataServiceErrorCode =
  | 'VALIDATION_ERROR'
  | 'NOT_FOUND'
  | 'ALREADY_EXISTS'
  | 'FORBIDDEN'
  | 'READ_ONLY'
  | 'SSRF_BLOCKED'
  | 'CONNECTION_FAILED'
  | 'TIMEOUT'
  | 'QUERY_REJECTED'
  | 'SCHEMA_ERROR'
  | 'UNKNOWN_COLUMN'
  | 'UNKNOWN_TABLE'
  | 'CONSOLE_DISABLED'
  | 'LIMIT_EXCEEDED'
  | 'PRODUCTION_CONFIRMATION_REQUIRED'
  | 'INTERNAL_ERROR';

export const DATA_SERVICE_ERROR_MESSAGES: Record<DataServiceErrorCode, string> = {
  VALIDATION_ERROR: 'The request was rejected by validation.',
  NOT_FOUND: 'That record no longer exists.',
  ALREADY_EXISTS: 'A record with those details already exists.',
  FORBIDDEN: 'You do not have access to that.',
  READ_ONLY: 'That connection is read-only, so it cannot be changed.',
  SSRF_BLOCKED: 'That host is not allowed by the server policy.',
  CONNECTION_FAILED: 'The database connection could not be established.',
  TIMEOUT: 'The request timed out.',
  QUERY_REJECTED: 'The statement was rejected by the SQL policy.',
  SCHEMA_ERROR: 'The database rejected the statement.',
  UNKNOWN_COLUMN: 'That column does not exist on the table.',
  UNKNOWN_TABLE: 'That table does not exist on the connection.',
  CONSOLE_DISABLED: 'The SQL console is switched off for that connection.',
  LIMIT_EXCEEDED: 'The result is larger than the data service allows.',
  PRODUCTION_CONFIRMATION_REQUIRED: 'Type the connection alias to confirm this production change.',
  INTERNAL_ERROR: 'The data service could not complete the request.',
};

/* ---------------------------------------------------------------- requests */

export interface DataConnectionRequest {
  alias: string;
  databaseName: string;
  engine: DataEngine;
  host: string;
  port: number;
  username: string;
  password?: string;
  schemaName?: string;
  environment: ConnectionEnvironment;
  defaultPermission: AccessPermission;
  allowConsole?: boolean;
  useSsl?: boolean;
  trustCertificate?: boolean;
  isShared?: boolean;
  connectTimeoutMs?: number;
  queryTimeoutSec?: number;
  rowCap?: number;
}

export interface DataConnectionTestRequest {
  engine: DataEngine;
  host: string;
  port: number;
  databaseName: string;
  schemaName?: string;
  username: string;
  password: string;
  useSsl?: boolean;
  trustCertificate?: boolean;
  connectTimeoutMs?: number;
}

export interface FilterCondition {
  column: string;
  operator: FilterOperator;
  value?: string;
  valueTo?: string;
  values?: string[];
}

export interface DataQueryRequest {
  connectionId: string;
  tableName: string;
  filters?: FilterCondition[];
  sortColumn?: string;
  sortDirection?: 'ASC' | 'DESC';
  columns?: string[];
  limit?: number;
  offset?: number;
  filterJoin?: 'AND' | 'OR';
}

export interface RawSqlRequest {
  connectionId: string;
  sql: string;
  limit?: number;
  confirmProductionAlias?: string;
}

export interface ValueSuggestionRequest {
  connectionId: string;
  tableName: string;
  columnName: string;
  prefix?: string;
  limit?: number;
}

export interface RowMutationRequest {
  connectionId: string;
  tableName: string;
  operation: RowOperation;
  key?: Record<string, unknown>;
  values?: Record<string, unknown>;
  confirmProductionAlias?: string;
}

export interface DataSavedQueryRequest {
  id?: string;
  name: string;
  description?: string;
  connectionId: string;
  tableName?: string;
  filtersJson?: string;
  rawSql?: string;
  sortJson?: string;
  limitValue?: number;
  isFavorite?: boolean;
}

export interface DataAccessRequest {
  id?: string;
  userId: string;
  connectionId: string;
  tablePattern?: string;
  permission: AccessPermission;
  expiresAt?: string;
}

/* --------------------------------------------------------------- responses */

export interface DataConnection {
  id: string;
  userId: string;
  alias: string;
  databaseName: string;
  engine: DataEngine;
  host: string;
  port: number;
  username: string;
  hasPassword: boolean;
  schemaName?: string;
  environment: ConnectionEnvironment;
  defaultPermission: AccessPermission;
  allowConsole?: boolean;
  useSsl?: boolean;
  trustCertificate?: boolean;
  isShared?: boolean;
  status: ConnectionStatus;
  lastTestedAt?: string;
  lastTestStatus?: ConnectionTestStatus;
  lastTestMessage?: string;
  connectTimeoutMs: number;
  queryTimeoutSec: number;
  rowCap: number;
  createdAt?: string;
  updatedAt?: string;
  effectivePermission?: AccessPermission;
  engineSupported: boolean;
}

export interface DataConnectionStats {
  total: number;
  development: number;
  staging: number;
  production: number;
  active: number;
  disabled: number;
  error: number;
  healthy: number;
  failing: number;
}

export interface ConnectionTestResult {
  status: ConnectionTestStatus;
  message: string;
  serverVersion?: string;
  driverName?: string;
  latencyMs?: number;
}

export interface ResultColumn {
  label: string;
  typeName?: string;
  jdbcType: number;
  nullable: boolean;
  primaryKey?: boolean;
  truncatedValue?: boolean;
}

export interface DataQueryResult {
  columns: ResultColumn[];
  rows: unknown[][];
  rowCount: number;
  truncated: boolean;
  durationMs: number;
  executedSql: string;
  tableName?: string;
  connectionAlias?: string;
  boundParameters?: Record<string, unknown>;
}

export interface TableMeta {
  tableName: string;
  tableType?: string;
  tableSchema?: string;
  rowCount: number;
  rowCountIsExact: boolean;
  sizeBytes: number;
  engine?: string;
  collation?: string;
  hasPrimaryKey: boolean;
  columnCount: number;
}

export interface TableMetaList {
  tables: TableMeta[];
  totalTables: number;
  totalApproximateRows: number;
  totalSizeBytes: number;
  rowCountsExact: boolean;
  durationMs: number;
}

export interface ColumnMeta {
  columnName: string;
  dataType?: string;
  typeName?: string;
  columnType?: string;
  nullable: boolean;
  primaryKey: boolean;
  autoIncrement: boolean;
  ordinalPosition: number;
  charMaxLength: number;
  numPrecision: number;
  numScale: number;
  columnDefault?: string;
  remarks?: string;
  controlKind?: FilterControlKind;
  enumValues?: string[];
  suggestionAllowed: boolean;
}

export interface ValueSuggestionResult {
  tableName: string;
  columnName: string;
  values: string[];
  totalReturned: number;
  capped: boolean;
  durationMs: number;
}

export interface QueryHistoryItem {
  id: string;
  connectionId: string;
  connectionAlias?: string;
  tableName?: string;
  executedSql: string;
  isRawSql: boolean;
  origin?: QueryOrigin;
  rowCount: number;
  durationMs: number;
  success: boolean;
  errorMessage?: string;
  createdAt: string;
}

export interface DataSavedQuery {
  id: string;
  name: string;
  description?: string;
  connectionId: string;
  connectionAlias?: string;
  connectionEnvironment?: string;
  tableName?: string;
  filtersJson?: string;
  rawSql?: string;
  hasRawSql: boolean;
  sortJson?: string;
  limitValue?: number;
  isFavorite: boolean;
  filtersVersion?: number;
  lastRunAt?: string;
  runCount?: number;
  createdAt?: string;
  updatedAt?: string;
  missingColumns?: string[];
}

export interface DataSavedQueryStats {
  total: number;
  favorites: number;
  withRawSql: number;
  onThisConnection: number;
}

export interface DataAccessGrant {
  id: string;
  userId: string;
  userEmail?: string;
  userName?: string;
  connectionId: string;
  connectionAlias?: string;
  connectionEnvironment?: string;
  tablePattern?: string;
  permission: AccessPermission;
  grantedBy?: string;
  grantedByName?: string;
  grantedAt?: string;
  expiresAt?: string;
  revokedAt?: string;
  effectivePermission?: AccessPermission;
  expired: boolean;
  connectionIsReadOnly: boolean;
}

export interface DataAccessStats {
  totalActive: number;
  readOnlyGrants: number;
  readWriteGrants: number;
  expiringSoon: number;
}

export interface DataServiceErrorBody {
  code?: DataServiceErrorCode;
  message?: string;
  detail?: string;
  sql?: string;
  timestamp?: string;
}

export interface GrantableUser {
  id: string;
  email: string;
  role: string;
  firstName?: string;
  lastName?: string;
  status?: string;
}

/* ------------------------------------------------- per-owner table configuration */

/**
 * `COMPANY` is inherited by every recruiter of that company. `USER` is the only
 * way a candidate can be granted anything, because a candidate has no company
 * row to inherit from.
 */
export type DataServiceConfigScope = 'COMPANY' | 'USER';

/**
 * `ALL` is the default posture. `SELECTED` narrows the connection down to the
 * patterns in {@link DataServiceConfig.tables}, where `*` and `?` stand in for
 * SQL's `%` and `_`.
 */
export type TableAccessMode = 'ALL' | 'SELECTED';

/**
 * How a configuration came to exist. Both live in the same list on the server and
 * are enforced the same way; the distinction exists so Team Access can answer
 * "who gave this company access, and did anyone pay for it?".
 */
export type DataServiceConfigSource = 'MANUAL' | 'SUBSCRIPTION';

export const CONFIG_SOURCE_OPTIONS: { label: string; value: DataServiceConfigSource; hint: string }[] = [
  {
    label: 'Manual Access',
    value: 'MANUAL',
    hint: 'Configured by an admin in Team Access.',
  },
  {
    label: 'Payment / Subscription Automation',
    value: 'SUBSCRIPTION',
    hint: 'Created automatically when a company paid for a Data Service plan.',
  },
];

export interface DataServiceConfigRequest {
  scope: DataServiceConfigScope;
  companyId?: string;
  userId?: string;
  connectionId: string;
  tableAccessMode: TableAccessMode;
  /** Table names or patterns. Required when the mode is `SELECTED`. */
  tables?: string[];
  /** Grant the structured query builder. Defaults to true. */
  allowQueryBuilder?: boolean;
  /** Grant the raw SQL console. Ignored on connections without it. */
  allowConsole?: boolean;
  /** Optional revocation date. Must be in the future when supplied. */
  expiresAt?: string;
}

export interface DataServiceConfig {
  id: string;
  scope: DataServiceConfigScope;
  companyId?: string;
  companyName?: string;
  userId?: string;
  userEmail?: string;
  userName?: string;
  userRole?: string;
  connectionId: string;
  connectionAlias?: string;
  connectionEnvironment?: ConnectionEnvironment;
  tableAccessMode: TableAccessMode;
  permission: AccessPermission;
  tables: string[];
  /** Whether the query builder is granted. Defaults to true server-side. */
  allowQueryBuilder: boolean;
  /** Whether the console is granted, and the connection allows it. */
  allowConsole: boolean;
  /**
   * False when the connection itself has the console switched off, so the row
   * can explain that the request is stored but cannot take effect yet.
   */
  consoleAvailableOnConnection: boolean;
  grantedBy?: string;
  grantedByName?: string;
  grantedAt?: string;
  expiresAt?: string;
  revokedAt?: string;
  /**
   * Whether a super admin configured this row or a verified payment created it.
   * Decides which Team Access tab lists it. Presentation only - the server enforces
   * both identically.
   */
  source: DataServiceConfigSource;
  /** The plan behind a SUBSCRIPTION row. Absent on MANUAL rows. */
  planId?: number;
  /** Snapshot of the plan name, so the row still names what was bought. */
  planName?: string;
  /** The verified payment behind a SUBSCRIPTION row. Absent on MANUAL rows. */
  paymentId?: string;
  /** Past the expiry but not yet revoked: shown separately from `active`. */
  expired: boolean;
  active: boolean;
  /**
   * False when the row is visible but the caller may not change it - another
   * company's row, a company-wide row seen by a recruiter, or a candidate.
   */
  manageableByCaller: boolean;
}

export interface DataServiceConfigStats {
  totalActive: number;
  companyScoped: number;
  userScoped: number;
  restrictedToTables: number;
  allTables: number;
}

/**
 * Who the signed-in user is, and who the server will let them configure.
 *
 * <p>The form does not branch on a role read out of local storage: everything
 * the UI needs to know about the caller's authority arrives here from the
 * server, which derives it from live company membership.</p>
 */
export interface ConfigurableSubject {
  userId: string;
  email: string;
  name?: string;
  role: string;
  /** False for the caller's own row, which is displayed but not a useful target. */
  configurable: boolean;
}

export interface ConfigurableContext {
  userId: string;
  role: string;
  companyId?: string;
  companyName?: string;
  /** True only for a super admin, who may configure any company. */
  canManageCompany: boolean;
  subjects: ConfigurableSubject[];
}

/** Mirrors the shape returned by GET /companies. */
export interface ConfigurableCompany {
  id: string;
  companyName?: string;
  companyDomain?: string;
  industry?: string;
}

/** Whether the signed-in user has a data service, for the navigation entry. */
export interface MyDataServiceAccess {
  hasAccess: boolean;
  connectionCount: number;
  connections: {
    connectionId: string;
    alias?: string;
    environment?: string;
    /** The posture they actually hold, after company inheritance. */
    tableAccessMode: TableAccessMode;
    /** The two surfaces they hold on this specific connection. */
    queryBuilder: boolean;
    console: boolean;
  }[];
  /** True when at least one reachable connection grants the query builder. */
  queryBuilderAvailable: boolean;
  /** True when at least one connection has both halves of the console. */
  consoleAvailable: boolean;
}

export interface ConfigListFilters {
  search?: string;
  scope?: DataServiceConfigScope;
  /** Narrows to hand-configured rows or to rows a verified payment created. */
  source?: DataServiceConfigSource;
  companyId?: string;
  userId?: string;
  connectionId?: string;
  /** Show expired and revoked rows too. */
  includeInactive?: boolean;
}

export const TABLE_ACCESS_MODE_OPTIONS: { label: string; value: TableAccessMode; hint: string }[] = [
  { label: 'All tables', value: 'ALL', hint: 'Every table on the connection, subject to query limits.' },
  {
    label: 'Selected tables',
    value: 'SELECTED',
    hint: 'Only the named tables. * and ? match like SQL wildcards.',
  },
];

export const CONFIG_SCOPE_OPTIONS: { label: string; value: DataServiceConfigScope; hint: string }[] = [
  { label: 'Whole company', value: 'COMPANY', hint: 'Inherited by every recruiter in the company.' },
  { label: 'One user', value: 'USER', hint: 'A recruiter, or a candidate granted by an admin.' },
];

/* ------------------------------------------------------------ display maps */

/**
 * Only MySQL is wired up: the backend ships a MySqlDialect and SqlDialectFactory
 * throws for every other engine, so the picker must not offer them.
 */
export const ENGINE_OPTIONS: { label: string; value: DataEngine }[] = [
  { label: 'MySQL', value: 'MYSQL' },
];

export const ENVIRONMENT_OPTIONS: { label: string; value: ConnectionEnvironment }[] = [
  { label: 'Development', value: 'DEVELOPMENT' },
  { label: 'Staging', value: 'STAGING' },
  { label: 'Production', value: 'PRODUCTION' },
];

export const PERMISSION_OPTIONS: { label: string; value: AccessPermission }[] = [
  { label: 'Read only', value: 'READ' },
  { label: 'Read / write', value: 'READ_WRITE' },
];

export const CONNECTION_STATUS_STYLES: Record<string, string> = {
  ACTIVE: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
  DISABLED: 'border-slate-700 bg-slate-950 text-slate-400',
  ERROR: 'border-rose-500/30 bg-rose-500/10 text-rose-400',
};

export const ENVIRONMENT_STYLES: Record<string, string> = {
  DEVELOPMENT: 'border-sky-500/30 bg-sky-500/10 text-sky-400',
  STAGING: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
  PRODUCTION: 'border-rose-500/30 bg-rose-500/10 text-rose-400',
};

export const TEST_STATUS_STYLES: Record<string, string> = {
  OK: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
  AUTH_FAILED: 'border-rose-500/30 bg-rose-500/10 text-rose-400',
  UNREACHABLE: 'border-rose-500/30 bg-rose-500/10 text-rose-400',
  DB_NOT_FOUND: 'border-rose-500/30 bg-rose-500/10 text-rose-400',
  SSL_ERROR: 'border-rose-500/30 bg-rose-500/10 text-rose-400',
  TIMEOUT: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
  DRIVER_MISSING: 'border-rose-500/30 bg-rose-500/10 text-rose-400',
  UNKNOWN: 'border-slate-700 bg-slate-950 text-slate-400',
};

/** Operators that take no value at all. */
export const NO_VALUE_OPERATORS: FilterOperator[] = ['IS_NULL', 'IS_NOT_NULL'];

/** Operators that take a second value. */
export const RANGE_OPERATORS: FilterOperator[] = ['BETWEEN'];

/** Operators that take a list of values. */
export const LIST_OPERATORS: FilterOperator[] = ['IN'];

export const FILTER_OPERATOR_OPTIONS: { label: string; value: FilterOperator }[] = [
  { label: 'Equals', value: 'EQ' },
  { label: 'Does not equal', value: 'NEQ' },
  { label: 'Contains', value: 'CONTAINS' },
  { label: 'Starts with', value: 'STARTS_WITH' },
  { label: 'Ends with', value: 'ENDS_WITH' },
  { label: 'Greater than', value: 'GT' },
  { label: 'Greater or equal', value: 'GTE' },
  { label: 'Less than', value: 'LT' },
  { label: 'Less or equal', value: 'LTE' },
  { label: 'Between', value: 'BETWEEN' },
  { label: 'Is null', value: 'IS_NULL' },
  { label: 'Is not null', value: 'IS_NOT_NULL' },
  { label: 'Is one of', value: 'IN' },
];

/** Hint shown under a filter row so the value shape matches the column type. */
export const CONTROL_KIND_HINTS: Record<FilterControlKind, string> = {
  TEXT: 'Free text. Containment operators match a substring.',
  NUMBER: 'Numeric. Use a plain number, or a range for Between.',
  DATE: 'Date only, as YYYY-MM-DD.',
  DATETIME: 'Date and time, as YYYY-MM-DD HH:MM:SS.',
  BOOLEAN: 'Boolean, as true or false.',
  ENUM: 'Pick one of the allowed values.',
  JSON: 'JSON text. Matching is textual, not structural.',
  BINARY: 'Binary or blob column. Value matching is not offered for these.',
  UUID: 'UUID, as 8-4-4-4-12 hex digits. Copy one from the grid to filter on it.',
};

/** Short label for the type chip, so a column can be read at a glance. */
export const CONTROL_KIND_LABELS: Record<FilterControlKind, string> = {
  TEXT: 'text',
  NUMBER: 'number',
  DATE: 'date',
  DATETIME: 'datetime',
  BOOLEAN: 'bool',
  ENUM: 'enum',
  JSON: 'json',
  BINARY: 'binary',
  UUID: 'uuid',
};

/**
 * One colour per column type, so the column list reads the same way the
 * environment and status badges do elsewhere in the module.
 */
export const CONTROL_KIND_STYLES: Record<FilterControlKind, string> = {
  TEXT: 'border-sky-500/30 bg-sky-500/10 text-sky-300',
  NUMBER: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
  DATE: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
  DATETIME: 'border-orange-500/30 bg-orange-500/10 text-orange-300',
  BOOLEAN: 'border-violet-500/30 bg-violet-500/10 text-violet-300',
  ENUM: 'border-teal-500/30 bg-teal-500/10 text-teal-300',
  JSON: 'border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-300',
  BINARY: 'border-slate-600 bg-slate-800/60 text-slate-300',
  UUID: 'border-indigo-500/30 bg-indigo-500/10 text-indigo-300',
};

/** Fallback when the backend sent no controlKind for a column. */
export const UNKNOWN_COLUMN_STYLE = 'border-slate-700 bg-slate-950 text-slate-400';
